import { sameWeek, type DraftWeek } from '../screens/settings/hoursForm'
import type { GoalDraft, GoalFields } from './planForm'

// Drafts outlive the Goals screen, which unmounts on every tab switch: she
// is interrupted between clients, and coming back must not cost her work.
// They are kept in sessionStorage too (review 2026-10-14), so an iPhone
// home-screen app that iOS reloads on the way back still has them; they go
// with the tab, and sign-out clears them.
//
// setup.mode says which wizard the answers belong to: first-time setup, or
// editing a goal she already has. Drafts saved by the old step-by-step
// wizard also carry a step; it is ignored, only the fields are read.
type SetupDraft = { fields: GoalFields; mode?: 'first' | 'edit' }
type Drafts = {
  setup: SetupDraft | null
  // week keeps the plan's windows as they were when she started editing
  // (base) alongside her edits (draft), so a fetch that turns up different
  // windows underneath her (saved elsewhere, or by someone else) can tell a
  // stale draft from a still-valid one instead of silently overwriting it.
  week: { draft: DraftWeek; base: DraftWeek } | null
  // goal is the take-home target etc. entered during first-time setup, kept
  // until the plan is actually saved with it.
  goal: GoalDraft | null
}

const KEY = 'harmovi.goalsDraft'
let drafts: Drafts | null = null

function read(): Drafts {
  if (drafts) return drafts
  drafts = { setup: null, week: null, goal: null }
  try {
    const saved = JSON.parse(sessionStorage.getItem(KEY) ?? 'null')
    if (saved && typeof saved === 'object') drafts = { setup: saved.setup ?? null, week: saved.week ?? null, goal: saved.goal ?? null }
  } catch {
    // No storage (private mode, tests): memory only.
  }
  return drafts
}

function write(next: Drafts): void {
  drafts = next
  try {
    if (!next.setup && !next.week && !next.goal) sessionStorage.removeItem(KEY)
    else sessionStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    // Memory only.
  }
}

export const goalsDraft = {
  setup: (mode: 'first' | 'edit' = 'first'): SetupDraft | null => {
    const s = read().setup
    return s && (s.mode ?? 'first') === mode ? s : null
  },
  saveSetup: (next: SetupDraft) => { write({ ...read(), setup: next }) },
  clearSetup: () => { write({ ...read(), setup: null }) },
  week: () => read().week,
  saveWeek: (draft: DraftWeek, base: DraftWeek) => { write({ ...read(), week: { draft, base } }) },
  clearWeek: () => { write({ ...read(), week: null }) },
  goal: () => read().goal,
  saveGoal: (next: GoalDraft) => { write({ ...read(), goal: next }) },
  clearGoal: () => { write({ ...read(), goal: null }) },
  // clearAll drops every draft: on sign-out and on SESSION_ENDED, so one
  // account's unsaved Goals work never leaks into the next one signed in on
  // the same device.
  clearAll: () => { write({ setup: null, week: null, goal: null }) },
}

// shouldRestoreWeekDraft decides whether a stored week draft still belongs to
// the week the planner is opening on: it does only if the plan it was edited
// from (base) matches the plan's current windows (baseline). Otherwise the
// hours moved on underneath it and the draft is stale.
export function shouldRestoreWeekDraft(base: DraftWeek, baseline: DraftWeek): boolean {
  return sameWeek(base, baseline)
}
